import { IRawStyle, IStyle } from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { FontSizes, FontWeights, Sizes } from '../../themes/styles';
import { CommonStyles } from '../../themes/themes';

export interface IItemStyles {
  root: IStyle;
  labelButton: IStyle;
  label: IStyle;
  labelText: IStyle;
  secondLabel: IStyle;
  secondLabelText: IStyle;
  icon: IStyle;
  close: IStyle;
}

const tagStyle = (theme: IH2OTheme): IRawStyle => ({
  height: 20,
  lineHeight: 20,
  padding: '0 4px',
  borderRadius: Sizes.borderRadius,
  fontSize: FontSizes.xxsmall,
  fontWeight: FontWeights.semiBold,
  marginRight: 4,
  backgroundColor: theme.semanticColors?.tagBackground,
  color: theme.semanticColors?.tagText,
  ...CommonStyles.truncateString,
  '&.loading': {
    overflow: 'visible',
  },
});

export const itemStylesDefault = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    position: 'relative',
    display: 'inline-flex',
  },
  labelButton: {
    ...CommonStyles.containerButton,
    display: 'flex',
    fontWeight: 'inherit',
  },
  close: {
    paddingLeft: 8,
    marginRight: -8,
  },
  icon: {
    paddingRight: 4,
    fontSize: FontSizes.small,
    color: theme.palette?.gray300,
    display: '',
  },
});

export const itemStylesCategory = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.semanticColors?.categoryBackground,
    color: theme.semanticColors?.categoryText,
  },
});

export const itemStylesTag = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
  },
});

export const itemStylesTruncatedTag = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    maxWidth: '100%',
  },
  labelButton: {
    width: '100%',
  },
  label: {
    width: '100%',
  },
  labelText: {
    ...CommonStyles.truncateString,
  },
});

export const itemStylesNav = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    marginRight: 40,
    padding: 0,
  },
  labelText: {
    a: {
      color: theme.semanticColors?.textQuinary,
      textDecoration: 'none',
      textTransform: 'uppercase',
      fontSize: FontSizes.small,
      letterSpacing: 0.74,
      position: 'relative',
      display: 'inline-block',
    },
    '& .selected': {
      color: theme.semanticColors?.navText,
      '::after': {
        content: "''",
        height: 4,
        borderRadius: Sizes.borderRadius,
        position: 'absolute',
        display: 'block',
        width: '100%',
        left: 0,
        marginTop: '8px',
        backgroundColor: theme.semanticColors?.navSelectedBackground,
      },
    },
  },
});

export const itemStylesCircle = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    marginRight: 131,
    '&.selected': {
      '.h2o-Item-label': {
        border: `1px solid ${theme.palette?.primary500}`,
        backgroundColor: theme.palette?.white,
        '::before': {
          left: 35,
        },
      },
      '.h2o-Item-labelText': {
        backgroundColor: theme.palette?.primary500,
      },
    },
    '&.last': {
      '.h2o-Item-label': {
        '::before': {
          display: 'none',
        },
      },
    },
    '&.active': {
      '.h2o-Item-labelText': {
        backgroundColor: theme.palette?.primary500,
      },
    },
    '&.next-item-is-active': {
      '.h2o-Item-label': {
        '::before': {
          backgroundColor: theme.palette?.primary500,
        },
      },
    },
  },
  labelButton: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  label: {
    height: 36,
    width: 36,
    borderRadius: '50%',
    position: 'relative',
    border: `1px solid transparent`,
    '::before': {
      content: "''",
      display: 'block',
      position: 'absolute',
      height: 2,
      width: 139,
      backgroundColor: theme.palette?.primary200,
      top: 16,
      left: 31,
    },
  },
  labelText: {
    backgroundColor: theme.palette?.primary200,
    height: 28,
    width: 28,
    borderRadius: '50%',
    margin: 3,
    textAlign: 'center',
    lineHeight: 28,
    color: theme.palette?.textOverPrimary,
  },
  secondLabel: {
    color: theme.semanticColors?.textQuaternary,
    lineHeight: 17,
    marginTop: 4,
    position: 'relative',
  },
  secondLabelText: {
    position: 'absolute',
    whiteSpace: 'nowrap',
    transform: 'translateX(-50%)',
  },
});

export const itemStylesDropdown = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    height: 23,
    lineHeight: 23,
    marginBottom: 2,
    marginTop: 2,
  },
});

export const itemStylesSearch = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    height: 39,
    lineHeight: 39,
    borderRadius: 20,
    paddingRight: 14,
    paddingLeft: 14,
    marginRight: 16,
    backgroundColor: theme.palette?.gray200,
    color: theme.palette?.gray500,
    '&.selected': {
      backgroundColor: theme.palette?.primary100,
      color: theme.palette?.primary800,
    },
  },
  labelButton: {
    cursor: 'pointer',
    padding: '0 16px',
  },
  labelText: {
    fontWeight: FontWeights.medium,
  },
});
